import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnDestroy,
    Output
} from '@angular/core';
import {
    ControlValueAccessor,
    FormArray,
    FormControl,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
    Validators
} from '@angular/forms';
import {
    BatterySetDto,
    CompleteSortieCommand,
    DisplayableMissionDto,
    DO_NOTHING,
    EquipmentDto,
    FlightDocumentationSummary,
    FlyFreelyError,
    FlyFreelyLoggingService,
    MissionRoleDto,
    MissionService,
    NameValue,
    PersonRolesDto,
    PersonWithRoles,
    RpaDto,
    RpaTypeDto,
    SortieDto
} from '@flyfreely-portal-ui/flyfreely';
import {
    batterySetSearch,
    equipmentSearch,
    rpaSearch
} from '@flyfreely-portal-ui/resource-ui';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Point } from 'geojson';
import { PersonnelDialoguesService } from 'libs/personnel/src/lib/personnel-dialogues.service';
import moment from 'moment';
import { combineLatest, Subject } from 'rxjs';
import { map, startWith, takeUntil, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { MissionDialoguesService } from '../../mission-dialogues.service';
import { MissionRecordService } from '../../mission-record-edit/mission-record.service';
import { completionFields } from '../fields';
import { MissionCompletionPersonnelSelectDataService } from '../mission-completion-personel-select-data.service';
import {
    DisplayableResource,
    MissionCompletionService
} from '../mission-completion.service';
import {
    FlightLogFileWithRpa,
    MissionFlightLogsDirective
} from '../mission-flight-logs.directive';

interface GroupedResource extends DisplayableResource {
    missionStatus: 'From mission plan' | 'Others';
}
interface CraftWithStatus extends RpaDto {
    missionStatus: 'From mission plan' | 'Others';
}

interface BatterySetWithGrouping extends BatterySetDto {
    grouping?:
        | 'Associated with selected RPA'
        | 'Compatible with selected RPA'
        | 'Other';
}

interface EquipmentWithGrouping extends EquipmentDto {
    grouping?: 'Associated with selected RPA' | 'Other';
}
/**
 * The order in which we want association matches in select lists. Used in sorting functions.
 */
const associationSortPriority = [
    'Associated with selected RPA',
    'Compatible with selected RPA',
    'Other'
];

function coordinatesFormatValidator(control: FormControl) {
    const coordinatesPattern = /^-?\d+(\.\d+)?, -?\d+(\.\d+)?$/; // regex pattern for longitude, latitude

    if (!coordinatesPattern.test(control.value)) {
        return {
            invalidCoordinates:
                'Must be longitude, latitude, eg. "153.05110163, -27.49073772"'
        };
    }

    return null;
}

function findBatterySetRpaCompatibility(
    rpa: RpaTypeDto,
    batterySet: BatterySetWithGrouping
) {
    // create an object similar to the rpa.compatibleBatteryTypeRequirements for the battery set
    const setComposition = batterySet.batteries.reduce(
        (acc, battery) =>
            acc[battery.batteryTypeId] != null
                ? acc
                : {
                      ...acc,
                      [battery.batteryTypeId]: batterySet.batteries.filter(
                          b => b.batteryTypeId === battery.batteryTypeId
                      ).length
                  },
        {}
    );
    return rpa.compatibleBatteryTypeIdList.reduce((acc, batteryTypeId) => {
        return (
            acc &&
            rpa.compatibleBatteryTypeRequirements[batteryTypeId] ===
                setComposition[batteryTypeId]
        );
    }, true);
}

/**
 * Resolve the duration source to use given the validity of the available data.
 * @param durationSource the reported duration source
 * @param allowTimer is there valid timer data
 * @returns the resolved duration source
 */
function resolveDurationSource(
    durationSource: CompleteSortieCommand.DurationSource | null,
    allowTimer: boolean
) {
    switch (durationSource) {
        case 'LOGS':
            return 'LOGS';
        case 'TIMER':
            return allowTimer ? 'TIMER' : 'MANUAL';
        default:
            return 'MANUAL';
    }
}

@Component({
    selector: 'flight-edit',
    templateUrl: './flight-edit.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FlightEdit),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => FlightEdit),
            multi: true
        },
        MissionCompletionPersonnelSelectDataService
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FlightEdit implements ControlValueAccessor, Validator, OnDestroy {
    @Input() canUseEquipment: boolean;
    @Input() readonly: boolean;
    @Input() hasFlightLogging: boolean;
    @Input() hasAdditionalFlightFields: boolean;
    @Input() missionDate: FormControl;
    @Input() mission?: DisplayableMissionDto;
    @Input() historicalMission = false;
    @Input() hasFlightDocumentation = false;
    @Input() documentationSummary: FlightDocumentationSummary | null = null;

    @Output() remove = new EventEmitter<void>();
    @Output() showSortieDocumentation = new EventEmitter<void>();
    @Output() refresh = new EventEmitter<void>();

    isNarrowResolution = false;

    allowTimer: boolean;
    isHistoricalMission = false;

    validationFields: FormlyFieldConfig[] = completionFields;

    unlistedPilot?: string;
    unlistedRpa?: string;
    unlistedBatterySet?: string;
    unlistedEquipment?: string;

    missionRoles: MissionRoleDto[];
    crewRoles: MissionRoleDto[];

    allRpaTypes: RpaTypeDto[];

    uniqueLogId: string;

    availableRpas: CraftWithStatus[];
    availablePersonnel: GroupedResource[];
    personnel: PersonWithRoles[];
    availableBatterySets: BatterySetWithGrouping[];
    availableEquipment: EquipmentWithGrouping[];
    DocButtonText = '';
    crewLookup: { [id: number]: string };

    batterySetSearch = batterySetSearch;
    equipmentSearch = equipmentSearch;
    rpaSearch = rpaSearch;

    flight: SortieDto;
    flightLogsForFlight: FlightLogFileWithRpa[];

    duration: number;

    formGroup: FormGroup;
    takeOffLandingForm: FormGroup;
    onChange: (arg: any) => void = DO_NOTHING;
    onTouched: (arg: any) => void = DO_NOTHING;
    onValidate: (arg: any) => void = DO_NOTHING;

    private ngUnsubscribe$ = new Subject<void>();
    sortieStatuses: NameValue[];

    working = false;

    constructor(
        private missionCompletionService: MissionCompletionService,
        private missionCompletionPersonnelSelectDataService: MissionCompletionPersonnelSelectDataService,
        private missionRecordService: MissionRecordService,
        private missionDialoguesService: MissionDialoguesService,
        private flightLogs: MissionFlightLogsDirective,
        private changeDetector: ChangeDetectorRef,
        private logging: FlyFreelyLoggingService,
        private personDialoguesService: PersonnelDialoguesService
    ) {
        this.uniqueLogId = uuidv4();
        this.formGroup = new FormGroup({
            id: new FormControl(undefined),
            number: new FormControl(undefined),
            durationSource: new FormControl(undefined),
            manualDuration: new FormControl(undefined),
            notes: new FormControl(undefined),
            status: new FormControl(undefined, [Validators.required]),
            reason: new FormControl(undefined),
            pilotId: new FormControl(undefined),
            craftId: new FormControl(undefined),
            batterySetId: new FormControl(undefined),
            startTime: new FormControl(undefined),
            endTime: new FormControl(undefined),
            equipmentIds: new FormControl(undefined),
            manualStartTime: new FormControl(undefined),
            crew: new FormArray([]),
            manualTakeoffPoint: new FormControl(undefined),
            manualLandingPoint: new FormControl(undefined)
        });
        this.takeOffLandingForm = new FormGroup({
            takeOffCoordinates: new FormControl(
                undefined,
                coordinatesFormatValidator
            ),
            landingCoordinates: new FormControl(
                undefined,
                coordinatesFormatValidator
            )
        });

        this.missionCompletionPersonnelSelectDataService.working$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => {
                this.working = working;
                this.changeDetector.markForCheck();
            });
    }

    ngOnInit() {
        if (this.mission == null) {
            throw new Error('mission input is required but is not set');
        }

        this.sortieStatuses = MissionService.getSortieCompletionStatuses();

        this.formGroup.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(value => {
                this.onChange({
                    ...value
                });
            });

        this.formGroup.controls['status'].valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(status => {
                this.updateValidators(
                    status,
                    this.formGroup.controls['durationSource'].value
                );
            });

        this.formGroup.controls['durationSource'].valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(durationSource => {
                this.updateValidators(
                    this.formGroup.controls['status'].value,
                    durationSource
                );
            });

        this.formGroup.controls['manualStartTime'].valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(value => {
                this.formGroup.controls['manualStartTime'].setValue(value, {
                    onlySelf: true,
                    emitEvent: false,
                    emitModelToViewChange: true
                });
            });

        this.takeOffLandingForm.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(value => {
                const toPoint = (value: string) => {
                    if (value == null) {
                        return null as Point;
                    }
                    const coords = value
                        .split(',')
                        .map(c => parseFloat(c.trim()));
                    return <Point>{
                        coordinates: coords,
                        type: 'Point'
                    };
                };
                this.formGroup.controls['manualTakeoffPoint'].patchValue(
                    toPoint(value.takeOffCoordinates)
                );
                this.formGroup.controls['manualLandingPoint'].patchValue(
                    toPoint(value.landingCoordinates)
                );
                this.formGroup.updateValueAndValidity();
                this.changeDetector.detectChanges();
            });

        this.isHistoricalMission =
            this.mission.type === DisplayableMissionDto.Type.RETROSPECTIVE;

        this.refreshMissionResources();

        this.setupFlightLogs();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    /**
     * Synchronise all the data from the mission completion service.
     * All the observables are combined in a combineLatest to ensure
     * everything gets synced up before firing dependent functions.
     * We can use rxjs tap within the individual observables to grab their values as they emit
     */
    refreshMissionResources() {
        const missionService = this.isHistoricalMission
            ? this.missionRecordService
            : this.missionCompletionService;

        // create separate Observables for the resources we need to combine
        const rpaValues = combineLatest([
            missionService.availableRpas$,
            missionService.allRpas$
        ]).pipe(
            tap(([availableRpas, rpas]) => {
                const rpasWithStatus: CraftWithStatus[] = rpas.map(c => ({
                    ...c,
                    missionStatus: 'Others'
                }));
                const missionRpas: CraftWithStatus[] = availableRpas.map(c => ({
                    ...c,
                    missionStatus: 'From mission plan'
                }));
                this.availableRpas = missionRpas.concat(rpasWithStatus);
            })
        );

        let missionPersonnel: GroupedResource[] = [];

        missionService.availablePersonnel$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(availablePersonnel => {
                missionPersonnel = availablePersonnel.map(p => ({
                    ...p,
                    missionStatus: 'From mission plan'
                }));
                const excludedIds = availablePersonnel.map(p => p.id);
                this.missionCompletionPersonnelSelectDataService.setParameters(
                    this.mission.organisationId!,
                    PersonRolesDto.Roles.PILOT,
                    excludedIds
                );
            });

        this.missionCompletionPersonnelSelectDataService.personnel$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(personnel => {
                // TODO: this needs to be handled differently since it's a separate list
                this.personnel = personnel;

                const personnelLookup = personnel.reduce(
                    (acc, p) => ({
                        ...acc,
                        [p.id]: `${p.firstName} ${p.lastName}`
                    }),
                    {}
                );

                const missionCrewLookup = missionPersonnel.reduce(
                    (acc, p) => ({
                        ...acc,
                        [p.id]: p.name
                    }),
                    {}
                );

                this.crewLookup = { ...personnelLookup, ...missionCrewLookup };

                const personnelWithStatus: GroupedResource[] = personnel.map(
                    p => ({
                        id: p.id,
                        name: `${p.firstName} ${p.lastName}`,
                        missionStatus: 'Others'
                    })
                );
                this.availablePersonnel =
                    missionPersonnel.concat(personnelWithStatus);
            });

        missionService.missionRoles$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(roles => {
                this.missionRoles = roles;
                this.crewRoles = this.missionRoles.filter(
                    r => r.coreRole !== MissionRoleDto.CoreRole.PILOT_IN_COMMAND
                );
            });

        // Combine all tapped observables into another combineLatest so we can synchronise output to dependent functions
        combineLatest([
            rpaValues,
            missionService.availableBatterySets$.pipe(
                tap(batterySets => (this.availableBatterySets = batterySets))
            ),
            missionService.availableEquipment$.pipe(
                tap(equipment => (this.availableEquipment = equipment))
            ),
            missionService.missionRoles$.pipe(
                tap(roles => (this.missionRoles = roles))
            ),
            missionService.allRpaTypes$.pipe(
                tap(types => (this.allRpaTypes = types))
            ),
            this.formGroup.controls['craftId'].valueChanges.pipe(
                startWith(null as number)
            )
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(values => {
                this.changeDetector.markForCheck();
                // Only update the battery grouping once everything completes and has values.
                this.updateBatteryGrouping();
                this.updateEquipmentGrouping();
            });
    }

    /**
     * Manage the validators for the controls based on the new state.
     */
    private updateValidators(
        status: CompleteSortieCommand.Status,
        durationSource: CompleteSortieCommand.DurationSource
    ) {
        if (status === 'ABORTED') {
            this.formGroup.controls['pilotId'].clearValidators();
            this.formGroup.controls['craftId'].clearValidators();
            this.formGroup.controls['manualDuration'].clearValidators();
        } else {
            this.formGroup.controls['pilotId'].setValidators(
                Validators.required
            );
            this.formGroup.controls['craftId'].setValidators(
                Validators.required
            );

            if (durationSource === 'MANUAL') {
                this.formGroup.controls['manualDuration'].setValidators(
                    Validators.compose([Validators.required, Validators.min(1)])
                );
                this.formGroup.controls['manualStartTime'].setValidators(
                    Validators.required
                );
            } else {
                this.formGroup.controls['manualDuration'].clearValidators();
                this.formGroup.controls['manualStartTime'].clearValidators();
            }
        }
        this.formGroup.controls['pilotId'].updateValueAndValidity({
            emitEvent: false
        });
        this.formGroup.controls['craftId'].updateValueAndValidity({
            emitEvent: false
        });
        this.formGroup.controls['manualDuration'].updateValueAndValidity({
            emitEvent: false
        });
        this.formGroup.updateValueAndValidity();
        this.changeDetector.detectChanges();
    }

    registerOnChange(fn: (arg: any) => void) {
        this.onChange = fn;
    }

    writeValue(flight: SortieDto) {
        if (flight) {
            this.flight = flight;
            this.allowTimer =
                flight.startTime != null && flight.endTime != null;

            const durationSource = resolveDurationSource(
                flight.durationSource,
                this.allowTimer
            );

            this.duration = flight.duration ?? 0;

            this.formGroup.patchValue({
                id: flight.id,
                number: flight.number,
                durationSource: durationSource,
                manualDuration: flight.manualDuration,
                notes: flight.notes,
                status: flight.status,
                reason: flight.reason ?? '',
                pilotId: flight.pilotId,
                craftId: flight.craftId,
                batterySetId: flight.batterySetId,
                equipmentIds: flight.equipmentIds,
                startTime: flight.startTime ?? null,
                endTime: flight.endTime ?? null,
                manualStartTime:
                    flight.manualStartTime ?? this.missionDate.value ?? null
            });

            // Only need to set the coordinates here and the other formGroup will auto-populate
            this.takeOffLandingForm.patchValue({
                takeOffCoordinates: (flight.durationSource ===
                SortieDto.DurationSource.LOGS
                    ? flight.takeoffPoint?.coordinates
                    : flight.manualTakeoffPoint?.coordinates
                )
                    ?.map(c => c.toString())
                    .join(', '),
                landingCoordinates: (flight.durationSource ===
                SortieDto.DurationSource.LOGS
                    ? flight.landingPoint?.coordinates
                    : flight.manualLandingPoint?.coordinates
                )
                    ?.map(c => c.toString())
                    .join(', ')
            });

            if (flight.crew != null && flight.crew.length > 0) {
                flight.crew.forEach(member => {
                    const item = new FormControl({
                        missionRoleId: member.missionRoleId,
                        notes: member.notes,
                        personId: member.personId
                    });
                    this.flightCrew.push(item);
                });
                this.changeDetector.markForCheck();
            }

            if (
                flight.unlistedPilot != null &&
                this.formGroup.controls['pilotId'].value == null
            ) {
                this.unlistedPilot = `${flight.unlistedPilot.firstName} ${
                    flight.unlistedPilot.lastName ?? ''
                }`;
            }

            if (flight.unlistedRpa != null) {
                this.unlistedRpa = flight.unlistedRpa.name;
            }

            if (flight.unlistedBatterySet != null) {
                this.unlistedBatterySet = flight.unlistedBatterySet.name;
            }

            if (
                flight.unlistedEquipment != null &&
                flight.unlistedEquipment.length > 0
            ) {
                this.unlistedEquipment = flight.unlistedEquipment
                    .map(e => e.name)
                    .join(', ');
            }
        }

        if (
            flight.unlistedPilot != null &&
            this.formGroup.controls['pilotId'].value != null
        ) {
            this.unlistedPilot = `${flight.unlistedPilot.firstName} ${
                flight.unlistedPilot.lastName ?? ''
            }`;
        }

        if (flight.unlistedRpa != null) {
            this.unlistedRpa = flight.unlistedRpa.name;
        }

        if (flight.unlistedBatterySet != null) {
            this.unlistedBatterySet = flight.unlistedBatterySet.name;
        }

        if (
            flight.unlistedEquipment != null &&
            flight.unlistedEquipment.length > 0
        ) {
            this.unlistedEquipment = flight.unlistedEquipment
                .map(e => e.name)
                .join(', ');
        }

        if (flight === null) {
            this.formGroup.reset();
        }
        this.changeDetector.detectChanges();
        this.onValidate(this.formGroup.valid);
    }

    addCrew() {
        const item = new FormControl({
            missionRoleId: null,
            notes: null,
            personId: null
        });
        this.flightCrew.push(item);
        this.changeDetector.detectChanges();
    }

    // TODO: limit available roles and people to keep crew valid. (eg. only one RPIC allowed)
    // TODO: allow saving flight crew
    canAddCrew() {
        if (this.flightCrew.controls.length === 0) {
            return true;
        }
        const last =
            this.flightCrew.controls[this.flightCrew.controls.length - 1].value;
        return last.missionRoleId != null && last.personId != null;
    }

    /**
     * A function that updates a list of associated batteries for the currently selected RPA type.
     * This is used to float relevant battery sets to the top in the battery set select
     */
    updateBatteryGrouping() {
        if (this.availableBatterySets == null) {
            return;
        }
        const selectedRpa = this.availableRpas?.find(
            r => r.id === this.formGroup.controls['craftId'].value
        );
        const selectedRpaType = this.allRpaTypes.find(
            type => selectedRpa?.rpaTypeId === type.id
        );
        const updatedBatteryGroupings: BatterySetWithGrouping[] =
            this.availableBatterySets
                .map(
                    set =>
                        <BatterySetWithGrouping>{
                            ...set,
                            grouping:
                                ((selectedRpaType == null ||
                                    selectedRpaType.compatibleBatteryTypeIdList
                                        .length === 0) &&
                                    (selectedRpa == null ||
                                        selectedRpa.associatedBatterySetIdList ==
                                            null ||
                                        selectedRpa.associatedBatterySetIdList
                                            .length === 0)) ||
                                set.batteries == null ||
                                set.batteries.length === 0
                                    ? null
                                    : selectedRpa.associatedBatterySetIdList.includes(
                                          set.id
                                      )
                                    ? 'Associated with selected RPA'
                                    : findBatterySetRpaCompatibility(
                                          selectedRpaType,
                                          set
                                      )
                                    ? 'Compatible with selected RPA'
                                    : 'Other'
                        }
                )
                .sort((a, b) => {
                    // Float 'Associated with selected RPA' to top, then 'Compatible with selected RPA' then Other, then ungrouped and sort alphabetically
                    const aIndex =
                        a.grouping == null
                            ? associationSortPriority.length + 1
                            : associationSortPriority.indexOf(a.grouping);
                    const bIndex =
                        b.grouping == null
                            ? associationSortPriority.length + 1
                            : associationSortPriority.indexOf(b.grouping);
                    if (
                        aIndex < bIndex ||
                        (aIndex === bIndex && b.name > a.name)
                    ) {
                        return -1;
                    } else if (aIndex === bIndex && b.name == a.name) {
                        return 0;
                    }
                    return +1;
                });
        this.availableBatterySets = updatedBatteryGroupings;
    }

    /**
     * A function that updates a list of associated batteries for the currently selected RPA type.
     * This is used to float relevant battery sets to the top in the battery set select
     */
    updateEquipmentGrouping() {
        if (this.availableEquipment == null) {
            return;
        }
        const selectedRpa = this.availableRpas?.find(
            r => r.id === this.formGroup.controls['craftId'].value
        );
        const updatedEquipmentGroupings: EquipmentWithGrouping[] =
            this.availableEquipment
                .map(
                    equipment =>
                        <EquipmentWithGrouping>{
                            ...equipment,
                            grouping:
                                selectedRpa == null ||
                                selectedRpa.associatedEquipmentIdList == null ||
                                selectedRpa.associatedEquipmentIdList.length ===
                                    0
                                    ? null
                                    : selectedRpa.associatedEquipmentIdList.includes(
                                          equipment.id
                                      )
                                    ? 'Associated with selected RPA'
                                    : 'Other'
                        }
                )
                .sort((a, b) => {
                    // Float 'Associated with selected RPA' to top, then 'Compatible with selected RPA' then Other, then ungrouped and sort alphabetically
                    const aIndex =
                        a.grouping == null
                            ? associationSortPriority.length + 1
                            : associationSortPriority.indexOf(a.grouping);
                    const bIndex =
                        b.grouping == null
                            ? associationSortPriority.length + 1
                            : associationSortPriority.indexOf(b.grouping);
                    if (
                        aIndex < bIndex ||
                        (aIndex === bIndex && b.name > a.name)
                    ) {
                        return -1;
                    } else if (aIndex === bIndex && b.name == a.name) {
                        return 0;
                    }
                    return +1;
                });
        this.availableEquipment = updatedEquipmentGroupings;
    }

    setupFlightLogs() {
        this.flightLogs.flightLogs$
            .pipe(
                startWith(null as FlightLogFileWithRpa[]),
                map(logs =>
                    logs != null && this.flight?.id != null
                        ? logs.filter(log => log.flightId === this.flight.id)
                        : []
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: logs => {
                    this.flightLogsForFlight = logs;
                    if (logs == null || logs.length === 0) {
                        return;
                    }
                    const startTime = logs.reduce(
                        (acc, l) =>
                            moment(l.startTime).isBefore(moment(acc))
                                ? l.startTime
                                : acc,
                        logs[0].startTime
                    );
                    // Prevent the form from marking as dirty on load if a flight log is already assigned
                    // by not emitting the patchValue event.
                    // If a log gets assigned by the user after the initial load, more form controls
                    // will be changed and this will mark the form as dirty and prompt the user to save.
                    this.formGroup.controls['startTime'].patchValue(startTime);
                    this.formGroup.controls['startTime'].markAsPristine();
                    this.changeDetector.detectChanges();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while finding flight log file for this flight`
                    );
                }
            });
    }

    showTakeoffLandingEdit() {
        const missionLocation = this.mission?.location;
        const organisationId = this.mission?.organisationId;
        const flight = this.flight;
        const takeoff = this.formGroup.value.manualTakeoffPoint?.coordinates;
        const landing = this.formGroup.value.manualLandingPoint?.coordinates;
        const modal =
            this.missionDialoguesService.showFlightTakeoffLandingEditDialogue(
                missionLocation,
                organisationId,
                flight,
                takeoff,
                landing,
                this.flightLogsForFlight.map(log => log.id)
            );

        modal.content.updatedTakeoffPoint
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(takeoff => {
                const floatArray = takeoff.map(t => t.toString()).join(', ');
                this.takeOffLandingForm.controls[
                    'takeOffCoordinates'
                ].patchValue(floatArray);
            });

        modal.content.updatedLandingPoint
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(landing => {
                const floatArray = landing.map(l => l.toString()).join(', ');
                this.takeOffLandingForm.controls[
                    'landingCoordinates'
                ].patchValue(floatArray);
            });
    }

    deleteCrew(i: number) {
        this.flightCrew.removeAt(i);
    }

    showPersonDetails(i: number) {
        this.personDialoguesService.showPersonDetailsDialogue(
            i,
            this.mission?.organisationId,
            false
        );
    }

    registerOnTouched(fn: (arg: any) => void) {
        this.onTouched = fn;
    }

    // communicate the inner form validation to the parent form
    validate(_: FormControl) {
        return this.formGroup.valid
            ? null
            : ({ flight: { valid: false } } as ValidationErrors);
    }

    registerOnValidatorChange(fn: () => void) {
        this.onValidate = fn;
    }

    onPilotSelected(pilot: GroupedResource) {
        if (pilot == null) {
            return;
        }
        const missionService = this.isHistoricalMission
            ? this.missionRecordService
            : this.missionCompletionService;
        missionService.addSelectedPilots([{ id: pilot.id, name: pilot.name }]);
    }

    onPilotSelectScroll() {
        this.missionCompletionPersonnelSelectDataService.onPersonSelectScroll();
    }

    onPilotSelectSearch(search: any) {
        this.missionCompletionPersonnelSelectDataService.onPersonSelectSearch(
            search
        );
    }

    onPilotSelectClose() {
        this.missionCompletionPersonnelSelectDataService.onPersonSelectClose();
    }

    get flightCrew() {
        return this.formGroup.get('crew') as FormArray;
    }

    get startTime() {
        return this.formGroup.get('startTime');
    }

    get endTime() {
        return this.formGroup.get('endTime');
    }

    refreshFlightEdit() {
        this.refresh.emit();
    }
}
